import React, {
  FunctionComponent,
  MouseEvent,
  PropsWithChildren,
  ReducerWithoutAction,
  useCallback,
  useMemo,
} from "react";
import { BaseEdge, EdgeProps, Handle, NodeProps, Position, useReactFlow } from "reactflow";
import styles from "./styles.module.scss";
import classNames from "classnames";
import {
  bfsTraversal,
  buildModelAdjacency,
  calculateMinLevel,
  calculateNodeCount,
  expandTableLineage,
  collapseBranch,
  hasCollapsibleBranch,
  highlightColumnConnections,
  highlightTableConnections,
  layoutElementsOnCanvas,
  setHighlightedTableConnections,
} from "../graph";
import {
  getColumnId,
  getColY,
  getSeeMoreId,
  VIEWS_TYPE_COLOR,
  toggleColumnEdges,
  toggleModelEdges,
  ViewsTypes,
  T_NODE_W,
} from "../utils";
import { TMoreTables } from "./MoreTables";

import {
  TestsIcon,
  EphemeralIcon,
  ExternalProjectIcon,
  SqlFilterIcon,
  SqlGroupByIcon,
  SqlInnerJoinIcon,
  SqlLeftJoinIcon,
  SqlLimitIcon,
  SqlOrderByIcon,
  SqlOuterJoinIcon,
  SqlRightJoinIcon,
  SqlCrossJoinIcon,
  SqlLateralJoinIcon,
  SqlFullJoinIcon,
  SqlUnionIcon,
  SqlSelectIcon,
  ArrowRightDoubleIcon,
  PostgresIcon,
  SnowflakeIcon,
  S3Icon,
  AlertErrorIcon,
  SeedIcon,
} from "../assets/icons";

import {
  COLUMNS_SIDEBAR,
  EXPOSURE_SIDEBAR,
  FUNCTION_SIDEBAR,
  SNOWFLAKE_STAGE_SIDEBAR,
  DASHBOARD_SIDEBAR,
  TABLES_SIDEBAR,
} from "../constants";
import { NODE_TYPE_SHORTHAND, NodeTypeIconForSQL } from "./Column";
import { NODE_TYPE_STYLES, NodeTypeIcon, TableNodePill } from "./Column";
import ChatStarterButton from "./ChatStarter";
import { Stack } from "@altimateai/lego";
import { Tooltip } from "@altimateai/lego";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import {
  mergeCollectColumns,
  mergeSeeMoreTables,
  setMinRange,
  setMoreTables,
  setNodeCount,
  setSelectedColumn,
  setSelectedTable,
  setSidebarScreen,
  setModalArgs,
  updateConfidenceWithOperatorList,
  setErrors,
} from "../context/LineageSlice";
import { showExternalViewCodeModal } from "../api";
import { CLL } from "../cllUtils";
import { useSelectTable } from "../hooks/useSelectTable";
import { Confidence, ColumnLineage, CollectColumn } from "../types";
import { CodeIcon } from "../../../assets/icons";
import ViewsTypeBadge from "./ViewsTypeBadge";
import IconClickable from "./IconClickable";
import { PercentValue } from "../context/types";
import { Typography } from "@altimateai/lego";
import { cn } from "../../../shared/utils";
import { IconExclamationCircle } from "@tabler/icons-react";
import dayjs from "dayjs";

const HANDLE_OFFSET = "-1px";

const BidirectionalHandles = () => (
  <>
    <Handle
      id="left"
      type="source"
      className="invisible"
      isConnectable={false}
      position={Position.Left}
      style={{ left: HANDLE_OFFSET }}
    />
    <Handle
      id="right"
      type="source"
      className="invisible"
      isConnectable={false}
      position={Position.Right}
      style={{ right: HANDLE_OFFSET }}
    />
    <Handle
      id="left"
      type="target"
      className="invisible"
      isConnectable={false}
      position={Position.Left}
      style={{ left: HANDLE_OFFSET }}
    />
    <Handle
      id="right"
      type="target"
      className="invisible"
      isConnectable={false}
      position={Position.Right}
      style={{ right: HANDLE_OFFSET }}
    />
    <Handle
      id="top"
      type="source"
      className="invisible"
      isConnectable={false}
      position={Position.Top}
      style={{ top: HANDLE_OFFSET }}
    />
    <Handle
      id="bottom"
      type="source"
      className="invisible"
      isConnectable={false}
      position={Position.Bottom}
      style={{ bottom: HANDLE_OFFSET }}
    />
    <Handle
      id="top"
      type="target"
      className="invisible"
      isConnectable={false}
      position={Position.Top}
      style={{ top: HANDLE_OFFSET }}
    />
    <Handle
      id="bottom"
      type="target"
      className="invisible"
      isConnectable={false}
      position={Position.Bottom}
      style={{ bottom: HANDLE_OFFSET }}
    />
  </>
);

export const TableNode: FunctionComponent<NodeProps> = ({ data }) => {
  const {
    label,
    table,
    url,
    upstreamCount,
    downstreamCount,
    nodeType,
    tests,
    materialization,
    isExternalProject,
    schema,
    stale,
    lastSeen,
  } = data;
  const flow = useReactFlow();

  const {
    state: {
      selectedTable,
      collectColumns,
      selectedColumn,
      leftExpansion,
      rightExpansion,
      selectCheck,
      nonSelectCheck,
      errors,
      client,
    },
    rerender,
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  const selectTable = useSelectTable();

  const _columnLen = Object.keys(collectColumns[table] || {}).length;
  const _showColumns = _columnLen > 0;
  const selected = selectedTable === table;

  const expand = async (right: boolean) => {
    if (CLL.inProgress) {
      CLL.showCllInProgressMsg();
      return;
    }
    let [nodes, edges] = await expandTableLineage(flow.getNodes(), flow.getEdges(), table, right);
    [nodes, edges] = highlightTableConnections(nodes, edges, selectedTable);
    layoutElementsOnCanvas(nodes, edges);
    flow.setNodes(nodes);
    flow.setEdges(edges);
    lineageDispatch(setMinRange(calculateMinLevel(nodes, edges, selectedTable)));
    lineageDispatch(
      setNodeCount(
        await calculateNodeCount(nodes, edges, selectedTable, leftExpansion, rightExpansion)
      )
    );
    rerender();
    if (!selectedColumn?.name) return;
    try {
      CLL.start();
      const currentEdges = flow.getEdges();
      // Model edges should be hidden when column lineage is selected
      toggleModelEdges(currentEdges, false);
      toggleColumnEdges(currentEdges, true);
      flow.setEdges(currentEdges);
      await bfsTraversal(
        nodes,
        edges,
        right,
        collectColumns[table].map(c => ({ table, name: c.column })),
        (operatorList: Confidence["operator_list"]) => {
          lineageDispatch(updateConfidenceWithOperatorList({ operatorList }));
        },
        (moreTables: ColumnLineage[]) => {
          lineageDispatch(mergeSeeMoreTables(moreTables));
        },
        (newCollectColumns: Record<string, CollectColumn[]>) => {
          lineageDispatch(mergeCollectColumns(newCollectColumns));
        },
        () => [flow.getNodes(), flow.getEdges()],
        (ns, es) => {
          flow.setNodes(ns);
          flow.setEdges(es);
        },
        selectedColumn,
        { direct: selectCheck, indirect: nonSelectCheck },
        (fn: ReducerWithoutAction<Record<string, string[]>>) =>
          lineageDispatch(setErrors(fn(errors)))
      );
      rerender();
    } catch (e) {
      console.log("cll:error:", e);
    } finally {
      CLL.end();
    }
  };

  const expandRight = () => expand(true);
  const expandLeft = () => expand(false);

  const collapse = async (right: boolean) => {
    if (CLL.inProgress) {
      CLL.showCllInProgressMsg();
      return;
    }
    try {
      let [nodes, edges] = collapseBranch(
        flow.getNodes(),
        flow.getEdges(),
        table,
        selectedTable,
        right
      );
      [nodes, edges] = highlightTableConnections(nodes, edges, selectedTable);
      layoutElementsOnCanvas(nodes, edges);
      flow.setNodes(nodes);
      flow.setEdges(edges);
      lineageDispatch(setMinRange(calculateMinLevel(nodes, edges, selectedTable)));
      lineageDispatch(
        setNodeCount(
          await calculateNodeCount(nodes, edges, selectedTable, leftExpansion, rightExpansion)
        )
      );
    } catch (e) {
      // calculateNodeCount is API-backed; don't leave an unhandled rejection or
      // skip the rerender if it fails after the graph was already mutated.
      console.error("collapse:error:", e);
    } finally {
      rerender();
    }
  };

  const collapseRight = () => collapse(true);
  const collapseLeft = () => collapse(false);

  const onDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (nodeType === "semantic_model") {
      return;
    }
    lineageDispatch(setSelectedTable(table));
    const sidebarMap: Record<string, string> = {
      exposure: EXPOSURE_SIDEBAR,
      function: FUNCTION_SIDEBAR,
      snowflake_stage: SNOWFLAKE_STAGE_SIDEBAR,
      dashboard: DASHBOARD_SIDEBAR,
    };
    lineageDispatch(setSidebarScreen(sidebarMap[nodeType] || COLUMNS_SIDEBAR));
  };

  const _edges = flow.getEdges();
  const _nodes = flow.getNodes();
  // Show a collapse handle only when it would actually prune something while
  // keeping the root (open-file) node and its lineage intact. Each check is a
  // full graph traversal, so build the adjacency once and memoize on a cheap
  // structural signature — the graph only changes shape on expand/collapse
  // (node/edge counts change), not on the frequent highlight-only rerenders.
  const collapseSignature = `${_nodes.length}:${_edges.length}:${table}:${selectedTable}`;
  const { canCollapseDownstream, canCollapseUpstream } = useMemo(() => {
    const adjacency = buildModelAdjacency(_nodes, _edges);
    return {
      canCollapseDownstream: hasCollapsibleBranch(
        _nodes,
        _edges,
        table,
        selectedTable,
        false,
        adjacency
      ),
      canCollapseUpstream: hasCollapsibleBranch(
        _nodes,
        _edges,
        table,
        selectedTable,
        true,
        adjacency
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseSignature]);
  const shownDownstream = _edges.filter(e => e.target === table).length;
  const shownUpstream = _edges.filter(e => e.source === table).length;
  const hasLeftSeeMore = !!flow.getNode(getSeeMoreId(table, false));
  const hasRightSeeMore = !!flow.getNode(getSeeMoreId(table, true));
  const canExpandLeft = downstreamCount > shownDownstream && !hasLeftSeeMore;
  const canExpandRight = upstreamCount > shownUpstream && !hasRightSeeMore;
  const tableId = table.replace(/[^a-zA-Z0-9]/g, "-");
  return (
    <div
      className="al-position-relative"
      style={{
        opacity: !selectedColumn?.name ? 1 : _showColumns ? 1 : 0.5,
      }}
    >
      {/*
        TableNode body click — see CLICK-SEMANTICS.md.
        Sets selected table, highlights lineage edges connected to it, and
        opens the model file in the editor. Does not invoke CLL; CLL is
        only triggered from `TableDetails.handleColumnClick` (the side
        panel). The `+` button on this same node is a separate target
        (expand-right-btn) and does not fire this handler.
      */}
      <div className={styles.table_node} onClick={() => selectTable(table, url)}>
        {client === "extension" && (
          <div className={styles.node_chat_action}>
            <ChatStarterButton level="model" table={table} tooltip="Chat about this model" />
          </div>
        )}
        <Stack
          direction="column"
          align="start"
          gap="xs"
          className={classNames(styles.header, {
            [styles.selected]: selected,
            [styles.collapse]: !_showColumns,
          })}
        >
          <Stack direction="column" align="start" gap="xs" className="al-w-full">
            <div className={styles.table_header}>
              {nodeType in NODE_TYPE_SHORTHAND && (
                <>
                  <div
                    className={classNames(
                      styles.node_icon,
                      NODE_TYPE_STYLES[nodeType as keyof typeof NODE_TYPE_STYLES]
                    )}
                  >
                    <NodeTypeIcon nodeType={nodeType} />
                    <div>{NODE_TYPE_SHORTHAND[nodeType as keyof typeof NODE_TYPE_STYLES]}</div>
                  </div>
                </>
              )}
              {nodeType in DIALECT_ICON && (
                <img
                  src={DIALECT_ICON[nodeType as keyof typeof DIALECT_ICON]}
                  className={styles.dialect_icon}
                  alt={`${nodeType} dialect icon`}
                />
              )}
              {nodeType === "snowflake_stage" && (
                <Stack direction="column" align="center" gap="none" className="al-text-primary">
                  <SeedIcon className="icon-sm" />
                  <Typography
                    variant="caption"
                    size="sm"
                    className="al-text-xs al-mb-0 al-text-primary"
                  >
                    STG
                  </Typography>
                </Stack>
              )}
              <div className="overflow-x">
                <div className={schema ? "text-overflow" : "lines-2"}>{label}</div>
                {schema && (
                  <div className="text-muted" style={{ fontSize: "0.75em" }}>
                    {schema}
                  </div>
                )}
              </div>
              {errors?.[table]?.length && (
                <Tooltip
                  title={
                    <div className={styles.error_tooltip}>
                      {errors[table].map((line, i) => (
                        <div key={i} className="mb-1">
                          {i + 1}. {line}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <AlertErrorIcon />
                </Tooltip>
              )}
            </div>
            <Stack
              direction="row"
              align="center"
              justify="between"
              gap="xs"
              className={classNames("al-w-full", styles.node_extra_info)}
            >
              <Stack direction="row" align="center" gap="xs">
                {canExpandLeft && (
                  <IconClickable
                    className={classNames("nodrag", styles.table_handle)}
                    onClick={e => {
                      e.stopPropagation();
                      expandLeft();
                    }}
                    testId={"expand-left-btn-" + table}
                    ariaLabel={`Expand left lineage for ${table}`}
                  >
                    +
                  </IconClickable>
                )}
                {canCollapseDownstream && (
                  <IconClickable
                    className={classNames("nodrag", styles.table_handle)}
                    onClick={e => {
                      e.stopPropagation();
                      collapseLeft();
                    }}
                    testId={"collapse-left-btn-" + table}
                    ariaLabel={`Collapse left lineage for ${table}`}
                  >
                    −
                  </IconClickable>
                )}
                {tests?.length > 0 && (
                  <TableNodePill
                    id={"table-node-tests-" + tableId}
                    icon={<TestsIcon />}
                    text={tests.length.toString()}
                    label="Tests"
                  />
                )}
                {materialization && (
                  <TableNodePill
                    id={"table-node-materilization-" + tableId}
                    icon={<EphemeralIcon />}
                    text={materialization}
                    label="Materialization"
                  />
                )}
                {stale && lastSeen && (
                  <TableNodePill
                    id={"table-node-stale-" + tableId}
                    icon={<IconExclamationCircle />}
                    text={"Stale"}
                    label={`Last seen on ${dayjs(lastSeen).format("DD MMM YYYY, hh:mm A")}`}
                  />
                )}
                {isExternalProject ? (
                  <TableNodePill
                    id={"table-node-is-external-" + tableId}
                    icon={<ExternalProjectIcon />}
                    text="ext"
                    label={`External Project: ${table}`}
                  />
                ) : null}
              </Stack>
              <Stack direction="row" align="center" gap="xs">
                <div
                  className={classNames(
                    "nodrag",
                    selected && nodeType !== "semantic_model" ? "text-blue" : "text-grey"
                  )}
                  onClick={onDetailsClick}
                  data-testid={"view-details-btn-" + table}
                >
                  Details
                </div>
                {canCollapseUpstream && (
                  <IconClickable
                    className={classNames("nodrag", styles.table_handle)}
                    onClick={e => {
                      e.stopPropagation();
                      collapseRight();
                    }}
                    testId={"collapse-right-btn-" + table}
                    ariaLabel={`Collapse right lineage for ${table}`}
                  >
                    −
                  </IconClickable>
                )}
                {canExpandRight && (
                  <IconClickable
                    className={classNames("nodrag", styles.table_handle)}
                    onClick={e => {
                      e.stopPropagation();
                      expandRight();
                    }}
                    testId={"expand-right-btn-" + table}
                    ariaLabel={`Expand right lineage for ${table}`}
                  >
                    +
                  </IconClickable>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Stack>
        {_showColumns && (
          <>
            <div className={styles.divider} />
            <div
              className={classNames(styles.content, {
                [styles.selected]: selected,
              })}
              style={{ height: getColY(_columnLen) }}
            />
          </>
        )}
      </div>

      <BidirectionalHandles />
    </div>
  );
};

export const DashboardNode: FunctionComponent<NodeProps> = ({ data }) => {
  const {
    dashboard_name,
    workbook_name,
    dashboard_url: _dashboard_url,
    description: _description,
    created_by: _created_by,
    last_modified: _last_modified,
    table,
  } = data;
  const flow = useReactFlow();

  const {
    state: {
      selectedTable,
      collectColumns: _collectColumns,
      selectedColumn,
      leftExpansion,
      rightExpansion,
      selectCheck: _selectCheck,
      nonSelectCheck: _nonSelectCheck,
      errors,
    },
    rerender: _rerender,
  } = useLineageContext();

  const lineageDispatch = useLineageDispatch();
  const selected = selectedTable === table;

  const onDetailsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    lineageDispatch(setSelectedTable(table));
    lineageDispatch(setSidebarScreen(DASHBOARD_SIDEBAR));
  };

  return (
    <div
      className="al-relative"
      style={{
        opacity: !selectedColumn?.name ? 1 : 0.5,
      }}
    >
      <div
        className={styles.table_node}
        onClick={async () => {
          const nodes = flow.getNodes();
          const edges = flow.getEdges();
          lineageDispatch(setMinRange(calculateMinLevel(nodes, edges, table)));
          lineageDispatch(
            setNodeCount(
              await calculateNodeCount(nodes, edges, table, leftExpansion, rightExpansion)
            )
          );
          setHighlightedTableConnections(flow, table);
          lineageDispatch(setSelectedTable(table));
        }}
      >
        <Stack
          direction="column"
          align="start"
          gap="xs"
          className={classNames(styles.header, {
            [styles.selected]: selected,
            [styles.collapse]: true, // Dashboard nodes don't show columns, so always collapsed
          })}
        >
          <Stack direction="column" align="start" gap="xs" className="al-w-full">
            <div className={styles.table_header}>
              <div
                className={classNames(
                  styles.node_icon,
                  NODE_TYPE_STYLES["dashboard" as keyof typeof NODE_TYPE_STYLES]
                )}
              >
                <NodeTypeIcon nodeType="dashboard" />
                <div>{NODE_TYPE_SHORTHAND["dashboard" as keyof typeof NODE_TYPE_SHORTHAND]}</div>
              </div>
              <div className="overflow-x">
                <div className="al-truncate">{dashboard_name}</div>
                {workbook_name && (
                  <div className="al-text-muted-foreground al-text-[0.75em]">{workbook_name}</div>
                )}
              </div>
              {errors?.[table]?.length && (
                <Tooltip
                  title={
                    <div className={styles.error_tooltip}>
                      {errors[table].map((line, i) => (
                        <div key={i} className="al-mb-1">
                          {i + 1}. {line}
                        </div>
                      ))}
                    </div>
                  }
                >
                  <AlertErrorIcon />
                </Tooltip>
              )}
            </div>
            <Stack
              direction="row"
              align="center"
              justify="between"
              gap="xs"
              className="al-w-full al-h-[1.6em]"
            >
              <Stack justify="end" className="al-w-full">
                <div
                  className={cn("nodrag", selected ? "al-text-brand" : "al-text-muted-foreground")}
                  onClick={onDetailsClick}
                  data-testid={"view-details-btn-" + table}
                >
                  Details
                </div>
              </Stack>
            </Stack>
          </Stack>
        </Stack>
      </div>
      <BidirectionalHandles />
    </div>
  );
};

const SavingsPerformanceBadge = ({
  percentValue,
  className,
}: PropsWithChildren<{ percentValue?: PercentValue; className: string }>) => {
  if (!percentValue) return null;
  return (
    <Tooltip title={percentValue.tooltip}>
      <div className={className}>
        <div className={styles.value}>{percentValue.value}</div>
        <div className={styles.percent}>{percentValue.percent}</div>
      </div>
    </Tooltip>
  );
};

export const SqlTableNode: FunctionComponent<NodeProps> = ({ data }) => {
  const { table, nodeType, label } = data;

  const {
    state: {
      sqlLineage,
      highlightedNodes,
      externalSidePanel,
      selectedNode,
      nodeSavingsPerformance,
      nodesCost,
    },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  const nType = (nodeType || "unknown") as keyof typeof NODE_TYPE_SHORTHAND;
  return (
    <div className="al-position-relative">
      <div className={styles.node_stats}>
        <SavingsPerformanceBadge
          percentValue={nodeSavingsPerformance[table]?.savings}
          className={styles.savings}
        />
        <SavingsPerformanceBadge
          percentValue={nodeSavingsPerformance[table]?.performance}
          className={styles.performance}
        />
      </div>
      <div
        className={styles.static_table_node}
        onClick={() => {
          document.dispatchEvent(new CustomEvent("selectedNode", { detail: table }));
        }}
      >
        <Stack
          direction="column"
          align="start"
          className={classNames(styles.header, {
            [styles.collapse]: true,
            [styles.highlighted]: highlightedNodes.includes(table),
            [styles.selected]: selectedNode === table,
          })}
        >
          <Stack
            direction="row"
            justify="between"
            align="center"
            gap="xxs"
            className="al-w-full al-px-2"
          >
            {nodesCost[table] && (
              <Tooltip title={nodesCost[table].tooltip}>
                <div className={classNames(styles.cost_data)}>
                  <div>{nodesCost[table].value}</div>
                  <div>{nodesCost[table].percent}</div>
                </div>
              </Tooltip>
            )}
            <div className={classNames(styles.node_icon, NODE_TYPE_STYLES[nType])}>
              <NodeTypeIconForSQL nodeType={nType} />
            </div>
            <div />
            <div className="lines-2">{label}</div>
            <div className="spacer" />
            <div />
            <div
              className={classNames(
                styles.details_btn,
                !sqlLineage || nType === "unknown" ? styles.disable : styles.enable
              )}
              onClick={e => {
                e.stopPropagation();
                if (nType === "unknown") return;
                if (externalSidePanel) return;
                lineageDispatch(setSelectedTable(table));
              }}
            >
              <ArrowRightDoubleIcon />
            </div>
          </Stack>
        </Stack>
      </div>
      <BidirectionalHandles />
    </div>
  );
};

export const SeeMoreNode: FunctionComponent<NodeProps> = ({ data }) => {
  const { tables = [], prevTable, right, level } = data as TMoreTables;
  const {
    state: { moreTables },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  const flow = useReactFlow();

  const handleClick = useCallback(
    (e: MouseEvent) => {
      e.stopPropagation();
      lineageDispatch(setSidebarScreen(TABLES_SIDEBAR));
      lineageDispatch(setMoreTables({ ...moreTables, tables, prevTable, right, level }));
    },
    [level, lineageDispatch, moreTables, prevTable, right, tables]
  );

  return (
    <div className={styles.see_more_node} onClick={handleClick}>
      <div className="fw-semibold">See more</div>
      <div className="spacer" />
      <div>{tables.filter(t => !flow.getNode(t.table)).length || ""}</div>
      <BidirectionalHandles />
    </div>
  );
};

export const SelfConnectingEdge: FunctionComponent<EdgeProps> = props => {
  const { sourceX, sourceY, targetX, targetY, markerEnd } = props;
  const radiusX = (sourceX - targetX) * 0.6;
  const radiusY = 50;
  const edgePath = `M ${sourceX - 5} ${sourceY} A ${radiusX} ${radiusY} 0 1 0 ${
    targetX + 2
  } ${targetY}`;

  return <BaseEdge path={edgePath} markerEnd={markerEnd} />;
};

export const ColumnNode: FunctionComponent<NodeProps> = ({ data }) => {
  const { column, table, viewsType, viewsCode, nodeType, datatype } = data;
  const {
    state: {
      selectedColumn,
      showCodeModal,
      refs,
      showRefs,
      enabledRefSources,
      inferenceConfidenceThreshold,
      collectColumns,
    },
  } = useLineageContext();
  const lineageDispatch = useLineageDispatch();
  const isSelected = selectedColumn?.table === table && selectedColumn?.name === column;
  // True when this column row was added to the canvas as part of a real CLL
  // trace (TableDetails.handleColumnClick → bfsTraversal populates
  // `collectColumns`). False when the row exists only because the user
  // toggled "show columns" on this table (which adds canvas nodes via
  // `applyTableExpansion` without touching `collectColumns`).
  // See CLICK-SEMANTICS.md for the full click contract.
  const isCllTracked = Boolean(collectColumns[table]?.some(c => c.column === column));

  const { isPk, isFk } = useMemo(() => {
    if (!showRefs) return { isPk: false, isFk: false };
    let pk = false;
    let fk = false;
    for (const ref of Object.values(refs)) {
      // Same filters the edge reconciler applies — keep the badge state
      // consistent with which refs actually render.
      if (!enabledRefSources[ref.source]) continue;
      if (ref.source === "inferred" && (ref.confidence ?? 0) < inferenceConfidenceThreshold) {
        continue;
      }
      if (ref.from.table === table && ref.from.columns.includes(column)) fk = true;
      if (ref.to.table === table && ref.to.columns.includes(column)) pk = true;
    }
    return { isPk: pk, isFk: fk };
  }, [refs, showRefs, enabledRefSources, inferenceConfidenceThreshold, table, column]);

  const viewsColor = viewsType && VIEWS_TYPE_COLOR[viewsType as ViewsTypes];
  const customStyles = viewsColor ? { borderColor: viewsColor } : {};
  const flow = useReactFlow();

  const selectTable = useSelectTable();

  /**
   * ColumnNode click is context-aware — see CLICK-SEMANTICS.md.
   *
   * - In a CLL session (column is in `collectColumns[table]`): re-target
   *   within the active trace. Drops the table-level selection and runs
   *   `highlightColumnConnections` to re-root the trace at this column.
   *
   * - In show-columns mode (column row was added by the chevron toggle, not
   *   by CLL): behave exactly like clicking the parent table — select it,
   *   highlight its lineage edges, open its source file. Because column
   *   rows are sibling DOM nodes (not children of the table node),
   *   ReactFlow doesn't bubble their clicks; without this, the visible
   *   "table card" loses click-targetability the moment you expand it.
   */
  const handleClick = () => {
    const currentNode = flow.getNode(getColumnId(table, column));
    if (!currentNode) {
      return;
    }
    if (isCllTracked) {
      lineageDispatch(setSelectedTable(""));
      lineageDispatch(setSelectedColumn({ name: column, table }));
      highlightColumnConnections(currentNode, flow);
      return;
    }
    // Show-columns mode → table-equivalent click. Look up the parent
    // table's URL via the flow instance (column nodes don't carry it).
    const parentUrl = (flow.getNode(table)?.data as { url?: string } | undefined)?.url;
    selectTable(table, parentUrl);
  };

  const hasCode = useMemo(() => {
    return (
      Object.values((viewsCode as Record<string, [string, string][]>) || {})
        .flat()
        .filter(([, query_hash]) => Boolean(query_hash))
        .map(([code]) => code).length > 0
    );
  }, [viewsCode]);

  return (
    <div
      className={classNames(styles.column_node, isSelected ? styles.selected : styles.default)}
      style={customStyles}
      onClick={handleClick}
    >
      <div className={styles.column_name}>{column}</div>
      <BidirectionalHandles />
      <div className={styles.column_top_right}>
        {hasCode && (
          <Tooltip title={"Click to view code"}>
            <div
              className={styles.column_code_icon}
              onClick={e => {
                e.stopPropagation();
                if (showCodeModal) {
                  lineageDispatch(
                    setModalArgs({
                      type: "views_code",
                      args: {
                        table,
                        viewsType,
                        viewsCode,
                        nodeType,
                        column,
                        datatype,
                      },
                    })
                  );
                } else {
                  showExternalViewCodeModal({
                    table,
                    viewsType,
                    viewsCode,
                    nodeType,
                    column,
                    datatype,
                    label: flow.getNode(table)?.data?.label,
                  });
                }
              }}
            >
              <CodeIcon />
            </div>
          </Tooltip>
        )}
        {viewsType && viewsType !== "Non select" && <ViewsTypeBadge viewsType={viewsType} />}
        {isPk && (
          <Tooltip title="Primary key — referenced by a relationships test">
            <span className={styles.pk_badge}>PK</span>
          </Tooltip>
        )}
        {isFk && (
          <Tooltip title="Foreign key — declared by a relationships test">
            <span className={styles.fk_badge}>FK</span>
          </Tooltip>
        )}
      </div>
    </div>
  );
};

export const SQL_ICONS: Record<string, React.ReactNode> = {
  INNER_JOIN: <SqlInnerJoinIcon />,
  OUTER_JOIN: <SqlOuterJoinIcon />,
  LEFT_JOIN: <SqlLeftJoinIcon />,
  FULL_JOIN: <SqlFullJoinIcon />,
  RIGHT_JOIN: <SqlRightJoinIcon />,
  CROSS_JOIN: <SqlCrossJoinIcon />,
  LATERAL_JOIN: <SqlLateralJoinIcon />,
  FILTER: <SqlFilterIcon />,
  GROUP: <SqlGroupByIcon />,
  LIMIT: <SqlLimitIcon />,
  SORT: <SqlOrderByIcon />,
  UNION: <SqlUnionIcon />,
  SELECT: <SqlSelectIcon className={"!al-size-6"} />,
};

export const DIALECT_ICON = {
  postgres: PostgresIcon,
  snowflake: SnowflakeIcon,
  s3: S3Icon,
};

export const OpNode: FunctionComponent<NodeProps> = ({ data }) => {
  const { type, expression, id } = data;
  const {
    state: {
      theme,
      highlightedNodes,
      externalSidePanel,
      selectedNode,
      nodeSavingsPerformance,
      nodesCost,
    },
  } = useLineageContext();
  const isDarkMode = theme === "dark";
  const lineageDispatch = useLineageDispatch();
  return (
    <div style={{ width: T_NODE_W, display: "flex", justifyContent: "center" }}>
      <BidirectionalHandles />
      <Stack direction="column">
        <div
          className={classNames(styles.op_node, {
            [styles.highlighted]: highlightedNodes.includes(id),
            [styles.selected]: selectedNode === id,
          })}
          onClick={() => {
            if (externalSidePanel) {
              document.dispatchEvent(new CustomEvent("selectedNode", { detail: id }));
            } else {
              lineageDispatch(
                setModalArgs({
                  type: "op_node",
                  args: { op_code: expression, op_type: type },
                })
              );
            }
          }}
        >
          {nodesCost[id] && (
            <Tooltip title={nodesCost[id].tooltip}>
              <div className={classNames(styles.cost_data)}>
                <div>{nodesCost[id].value}</div>
                <div>{nodesCost[id].percent}</div>
              </div>
            </Tooltip>
          )}
          <div
            className={classNames(
              styles.node_icon,
              isDarkMode ? styles.dark_mode : styles.light_mode
            )}
          >
            {SQL_ICONS[type]}
          </div>
          <div />
          <div className={styles.op_type_text}>
            <span>{type}</span>
            <div className={styles.node_stats}>
              <SavingsPerformanceBadge
                percentValue={nodeSavingsPerformance[id]?.savings}
                className={styles.savings}
              />
              <SavingsPerformanceBadge
                percentValue={nodeSavingsPerformance[id]?.performance}
                className={styles.performance}
              />
            </div>
          </div>
          <div />
        </div>
      </Stack>
    </div>
  );
};
